import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DEFAULT_FILTERS,
  HANDLER_TYPE_FILTERS,
  TASK_STATUSES,
  type TaskFilters,
  type TaskStatus,
} from '../types';

const VALID_PERIODS: TaskFilters['donePeriod'][] = ['24h', '7d', '30d', 'all'];

function parseStatusList(raw: string | null): TaskStatus[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s): s is TaskStatus => (TASK_STATUSES as readonly string[]).includes(s));
}

function parseList(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

function parsePeriod(raw: string | null): TaskFilters['donePeriod'] {
  if (!raw) return DEFAULT_FILTERS.donePeriod;
  return (VALID_PERIODS as string[]).includes(raw)
    ? (raw as TaskFilters['donePeriod'])
    : DEFAULT_FILTERS.donePeriod;
}

export interface UseTasksUrlStateReturn {
  filters: TaskFilters;
  setFilters: (next: Partial<TaskFilters>) => void;
  setHandlerTypeSelection: (handler: string, values: string[]) => void;
  clearFilters: () => void;
  selectedTaskId: string | null;
  setSelectedTaskId: (id: string | null) => void;
}

/**
 * Sincroniza filtros e task selecionada com a querystring.
 * Cada handler-tipo (agent/skill/tool/op/pipeline/wizard) tem sua chave própria.
 */
export function useTasksUrlState(): UseTasksUrlStateReturn {
  const [params, setParams] = useSearchParams();

  const filters: TaskFilters = useMemo(() => {
    const byHandler: Record<string, string[]> = {};
    for (const meta of HANDLER_TYPE_FILTERS) {
      const list = parseList(params.get(meta.slug));
      if (list.length) byHandler[meta.handler] = list;
    }
    return {
      q: params.get('q') ?? '',
      status: parseStatusList(params.get('status')),
      handler: parseList(params.get('handler')),
      byHandler,
      donePeriod: parsePeriod(params.get('period')),
      // P1.13 F7 — `hide_orphans=1` no querystring quando ativo.
      hideOrphans: params.get('hide_orphans') === '1',
    };
  }, [params]);

  const writeParams = useCallback(
    (mutate: (sp: URLSearchParams) => void) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setFilters = useCallback(
    (next: Partial<TaskFilters>) => {
      writeParams((sp) => {
        if (next.q !== undefined) {
          if (next.q.trim()) sp.set('q', next.q.trim());
          else sp.delete('q');
        }
        if (next.status !== undefined) {
          if (next.status.length) sp.set('status', next.status.join(','));
          else sp.delete('status');
        }
        if (next.handler !== undefined) {
          if (next.handler.length) sp.set('handler', next.handler.join(','));
          else sp.delete('handler');
        }
        if (next.byHandler !== undefined) {
          for (const meta of HANDLER_TYPE_FILTERS) {
            const v = next.byHandler[meta.handler] ?? [];
            if (v.length) sp.set(meta.slug, v.join(','));
            else sp.delete(meta.slug);
          }
        }
        if (next.donePeriod !== undefined) {
          if (next.donePeriod !== DEFAULT_FILTERS.donePeriod) sp.set('period', next.donePeriod);
          else sp.delete('period');
        }
        if (next.hideOrphans !== undefined) {
          if (next.hideOrphans) sp.set('hide_orphans', '1');
          else sp.delete('hide_orphans');
        }
      });
    },
    [writeParams],
  );

  const setHandlerTypeSelection = useCallback(
    (handler: string, values: string[]) => {
      const meta = HANDLER_TYPE_FILTERS.find((h) => h.handler === handler);
      if (!meta) return;
      writeParams((sp) => {
        if (values.length) sp.set(meta.slug, values.join(','));
        else sp.delete(meta.slug);
      });
    },
    [writeParams],
  );

  const clearFilters = useCallback(() => {
    writeParams((sp) => {
      sp.delete('q');
      sp.delete('status');
      sp.delete('handler');
      sp.delete('period');
      sp.delete('hide_orphans');
      for (const meta of HANDLER_TYPE_FILTERS) sp.delete(meta.slug);
    });
  }, [writeParams]);

  const selectedTaskId = params.get('task');
  const setSelectedTaskId = useCallback(
    (id: string | null) => {
      writeParams((sp) => {
        if (id) sp.set('task', id);
        else sp.delete('task');
      });
    },
    [writeParams],
  );

  return {
    filters,
    setFilters,
    setHandlerTypeSelection,
    clearFilters,
    selectedTaskId,
    setSelectedTaskId,
  };
}
